// Miky Motorsport - vstup: klavesnice + gamepad + input shaping
// Produkuje analogove controls {throttle, brake, steer, handbrake} s rampami a
// smoothingem, aby byl sim hratelny i na klavesnici (viz plan: povinny shaping).
import { ACTION, KEY_MAP, INPUT } from './constants.js';

// Posun current k target nejvyse o maxDelta (bez prestreleni). maxDelta=0 -> beze
// zmeny. Ciste, testovatelne; pouziva se pro vyhlazeni rejdu/plynu/brzdy.
export function moveToward(cur, target, maxDelta) {
  if (cur < target) return Math.min(cur + maxDelta, target);
  if (cur > target) return Math.max(cur - maxDelta, target);
  return cur;
}

// Tvarovani gamepad osy: pod deadzone -> 0; nad ni se zbytek [dz,1] preskaluje na
// [0,1] a aplikuje expo krivka (expo>1 = jemnejsi u stredu). Znamenko zachovano,
// magnitude orezana na 1. dz se predpoklada v [0,1).
export function shapeAxis(raw, deadzone, expo) {
  const a = Math.abs(raw);
  if (a <= deadzone) return 0;
  const mag = Math.min(1, (a - deadzone) / (1 - deadzone));
  return Math.sign(raw) * Math.pow(mag, expo);
}

// Vyber rychlosti rejdu (deg/s): najezd k cili (STEER_RATE_DPS) vs navrat do stredu
// (STEER_RETURN_DPS). Navratovy rate plati kdyz cil snizuje |rejd| nebo meni stranu;
// jinak (zvetsujeme rejd na stejne strane) plati najezdovy rate.
export function steerRateFor(steerTarget, steer) {
  const sameOrGrowing =
    Math.abs(steerTarget) >= Math.abs(steer) &&
    Math.sign(steerTarget) === Math.sign(steer || steerTarget);
  return sameOrGrowing ? INPUT.STEER_RATE_DPS : INPUT.STEER_RETURN_DPS;
}

export class InputManager {
  constructor() {
    this.held = new Set(); // aktualne drzene akce (klavesnice)
    this._pressedQueue = new Set(); // akce stisknute od minuleho update()
    this.justPressedSet = new Set();

    // Vyhlazene analogove hodnoty
    this.steer = 0; // -1..1
    this.throttle = 0; // 0..1
    this.brake = 0; // 0..1
    this.handbrake = false;

    this._gpIndex = null;
    this._gpPrevButtons = [];

    // preventDefault na mapovanych klavesach jen behem jizdy (RACE) - jinak by Space/
    // Enter/sipky/WASD byly potlaceny i v menu a rozbily klavesovou navigaci. Hra
    // prepina pres setCaptureGameKeys (zap pri vstupu do zavodu, vyp pri navratu do menu).
    this._captureGameKeys = false;

    this._onKeyDown = (e) => {
      const actions = KEY_MAP[e.code];
      if (!actions) return;
      // nech projit modifikatory pro devtools apod. jen kdyz nejsou herni klavesy
      if (e.code === 'F5' || e.code === 'F12') return;
      if (this._captureGameKeys) e.preventDefault();
      if (e.repeat) return;
      for (const a of actions) {
        this.held.add(a);
        this._pressedQueue.add(a);
      }
    };
    this._onKeyUp = (e) => {
      const actions = KEY_MAP[e.code];
      if (!actions) return;
      for (const a of actions) this.held.delete(a);
    };
    this._onBlur = () => {
      this.held.clear();
    };
    this._onGamepadConnected = (e) => {
      this._gpIndex = e.gamepad.index;
    };
    this._onGamepadDisconnected = (e) => {
      if (this._gpIndex === e.gamepad.index) this._gpIndex = null;
    };

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('blur', this._onBlur);
    window.addEventListener('gamepadconnected', this._onGamepadConnected);
    window.addEventListener('gamepaddisconnected', this._onGamepadDisconnected);
  }

  _gamepad() {
    if (this._gpIndex == null) {
      // auto-detekce prvniho pripojeneho
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      for (const p of pads) {
        if (p) {
          this._gpIndex = p.index;
          break;
        }
      }
    }
    if (this._gpIndex == null) return null;
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    return pads[this._gpIndex] || null;
  }

  // Volat jednou za tick (dt v sekundach)
  update(dt) {
    // edge detekce (klavesnice)
    this.justPressedSet = new Set(this._pressedQueue);
    this._pressedQueue.clear();

    const gp = this._gamepad();

    // --- cile vstupu ---
    let steerTarget = (this.held.has(ACTION.STEER_RIGHT) ? 1 : 0) - (this.held.has(ACTION.STEER_LEFT) ? 1 : 0);
    let throttleTarget = this.held.has(ACTION.THROTTLE) ? 1 : 0;
    let brakeTarget = this.held.has(ACTION.BRAKE) ? 1 : 0;
    let handbrake = this.held.has(ACTION.HANDBRAKE);

    if (gp) {
      const dz = INPUT.GAMEPAD_DEADZONE;
      const ax = gp.axes[0] || 0;
      if (Math.abs(ax) > dz) {
        steerTarget = shapeAxis(ax, dz, INPUT.GAMEPAD_STEER_EXPO);
      }
      const rt = gp.buttons[7] ? gp.buttons[7].value : 0; // RT throttle
      const lt = gp.buttons[6] ? gp.buttons[6].value : 0; // LT brake
      if (rt > 0.02) throttleTarget = Math.max(throttleTarget, rt);
      if (lt > 0.02) brakeTarget = Math.max(brakeTarget, lt);
      if (gp.buttons[0] && gp.buttons[0].pressed) handbrake = true; // A = rucni brzda

      // gamepad button edges -> justPressed
      this._gamepadEdge(gp, 3, ACTION.CAMERA); // Y
      this._gamepadEdge(gp, 2, ACTION.RESET); // X
      this._gamepadEdge(gp, 9, ACTION.PAUSE); // Start
      this._gpPrevButtons = gp.buttons.map((b) => b.pressed);
    }

    // --- shaping: rizeni (rate-limited) ---
    const steerRate = steerRateFor(steerTarget, this.steer);
    const steerStep = (steerRate / INPUT.STEER_MAX_DEG) * dt;
    this.steer = moveToward(this.steer, steerTarget, steerStep);

    // --- shaping: plyn / brzda (ramp) ---
    const tRate = throttleTarget > this.throttle ? 1 / INPUT.THROTTLE_RAMP_S : 1 / INPUT.THROTTLE_RELEASE_S;
    this.throttle = moveToward(this.throttle, throttleTarget, tRate * dt);
    const bRate = brakeTarget > this.brake ? 1 / INPUT.BRAKE_RAMP_S : 1 / INPUT.BRAKE_RELEASE_S;
    this.brake = moveToward(this.brake, brakeTarget, bRate * dt);

    this.handbrake = handbrake;
  }

  _gamepadEdge(gp, btnIdx, action) {
    const now = gp.buttons[btnIdx] && gp.buttons[btnIdx].pressed;
    const prev = this._gpPrevButtons[btnIdx];
    if (now && !prev) this.justPressedSet.add(action);
  }

  getControls() {
    return {
      throttle: this.throttle,
      brake: this.brake,
      steer: this.steer,
      handbrake: this.handbrake,
    };
  }

  justPressed(action) {
    return this.justPressedSet.has(action);
  }

  // A4: gamepad haptika (dual-rumble). Podporuje Chrome/Edge; jinde tichy no-op.
  // strong/weak 0..1 (silny/slaby motor), durMs delka vibrace.
  rumble(strong = 0.5, weak = 0.3, durMs = 150) {
    try {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      const act = pads[0] && pads[0].vibrationActuator;
      if (act && act.playEffect) {
        act.playEffect('dual-rumble', {
          duration: durMs, startDelay: 0,
          strongMagnitude: Math.min(1, Math.max(0, strong)),
          weakMagnitude: Math.min(1, Math.max(0, weak)),
        });
      }
    } catch (e) { /* haptika nepodporovana - ignoruj */ }
  }

  // Zap/vyp potlaceni vychoziho chovani mapovanych klaves (preventDefault).
  // Zapnout pri vstupu do zavodu, vypnout v menu/garazi/karieře (klavesova navigace).
  setCaptureGameKeys(v) {
    this._captureGameKeys = !!v;
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    window.removeEventListener('gamepadconnected', this._onGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this._onGamepadDisconnected);
  }
}
